/**
 * audioNotifications — M2.2.4 · Stato audio globale subscribable.
 *
 * Root cause del bug M2.2.3: il vecchio hook usava un listener "invisibile"
 * sul primo click/keydown per sbloccare AudioContext. Se l'utente apriva
 * /staff/messages tramite deep-link e non cliccava mai nulla, `unlocked`
 * restava false → `playBeep` restituiva silent → nessun feedback all'utente.
 *
 * Fix definitivo: stato ESPLICITO con UI control.
 *
 * Stati:
 *   "unsupported" → browser senza WebAudio
 *   "off"         → utente ha disabilitato via UI (preferenza persistita)
 *   "suspended"   → autoplay policy → richiede click esplicito
 *   "running"     → pronto a suonare beep su nuovi inbound
 *
 * L'utente vede sempre il vero stato e può abilitarlo con 1 click.
 */

const LS_KEY = "sb.audio.pref";   // "on" | "off" (default implicito: on)

class AudioNotifications {
  constructor() {
    this.ctx = null;
    this.state = "suspended";     // ottimista finché non testiamo
    this.listeners = new Set();
    this._detect();
    // M2.3.1 · Default ON: se NON esiste una preferenza esplicita "off",
    // trattiamo come ON. L'autoplay policy tiene comunque state="suspended"
    // finché non arriva un gesture → poi enable() lo porta a "running".
    try {
      const p = localStorage.getItem(LS_KEY);
      if (p === "off") this.state = "off";
      // else → suspended (default ON, in attesa gesture)
    } catch { /* ignore */ }
    // Auto-unlock al primo gesture globale (se pref non è OFF esplicito).
    if (this.state !== "off" && this.state !== "unsupported"
        && typeof window !== "undefined") {
      const tryUnlock = () => {
        if (this.state === "off" || this.state === "unsupported") return;
        this.enable().catch(() => { /* noop */ });
      };
      const opts = { once: true, capture: true, passive: true };
      window.addEventListener("pointerdown", tryUnlock, opts);
      window.addEventListener("keydown", tryUnlock, opts);
      window.addEventListener("touchstart", tryUnlock, opts);
    }
  }

  _detect() {
    if (typeof window === "undefined") { this.state = "unsupported"; return; }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) { this.state = "unsupported"; }
  }

  /**
   * Recovery cross-device: aggancia listener `statechange` sull'AudioContext
   * per rilevare quando iOS/iPadOS lo mette in "interrupted"/"suspended"
   * (chiamata telefonica, Siri, blocco schermo prolungato).
   *
   * Sync lo stato UI (`suspended`) senza toccare `_chime5s`/`play` (invarianti
   * M2.3.5). Il chime resta funzionante finché `ctx.state === "running"`.
   * Se lo state cambia, `AudioStatusBar` invita l'utente a re-abilitare con
   * un tap.
   */
  _attachCtxStateListener() {
    if (!this.ctx || this._ctxListenerBound) return;
    this._ctxListenerBound = true;
    try {
      this.ctx.addEventListener("statechange", () => {
        if (this.state === "off" || this.state === "unsupported") return;
        const s = this.ctx.state;   // "running" | "suspended" | "closed" | "interrupted" (iOS)
        this._setState(s === "running" ? "running" : "suspended");
      });
    } catch { /* silent */ }
  }

  /**
   * Riprova a sbloccare dopo recovery (lifecycle → onRecovery).
   * NON crea un nuovo context (evita perdita dei gesture accumulati).
   * Chiamare solo dentro un contesto gesture-safe o subito dopo.
   */
  async tryResumeAfterRecovery() {
    if (this.state === "off" || this.state === "unsupported") return false;
    if (!this.ctx) return false;
    if (this.ctx.state === "running") { this._setState("running"); return true; }
    try {
      await this.ctx.resume();
      this._setState(this.ctx.state === "running" ? "running" : "suspended");
      return this.state === "running";
    } catch {
      this._setState("suspended");
      return false;
    }
  }

  subscribe(fn) {
    this.listeners.add(fn);
    // Fire immediato con stato corrente
    try { fn(this.state); } catch {}
    return () => this.listeners.delete(fn);
  }

  _setState(s) {
    if (this.state === s) return;
    this.state = s;
    this.listeners.forEach((fn) => { try { fn(s); } catch {} });
  }

  /**
   * Enable audio — DEVE essere chiamato all'interno di un event handler
   * di user gesture (click/keydown/touchstart) altrimenti il browser
   * lascia AudioContext in stato suspended.
   */
  async enable() {
    if (this.state === "unsupported") return false;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!this.ctx) this.ctx = new AC();
      this._attachCtxStateListener();
      if (this.ctx.state === "suspended") {
        await this.ctx.resume();
      }
      const actual = this.ctx.state;   // "running" | "suspended" | "closed"
      this._setState(actual === "running" ? "running" : "suspended");
      try { localStorage.setItem(LS_KEY, "on"); } catch {}
      if (this.state === "running") {
        this._testChirp();
      }
      return this.state === "running";
    } catch (e) {
      this._setState("suspended");
      return false;
    }
  }

  disable() {
    this._setState("off");
    try { localStorage.setItem(LS_KEY, "off"); } catch {}
    // NON chiudiamo il context — l'utente potrebbe riabilitare senza gesture.
  }

  /**
   * Suona il chime se stato "running" e pagina visibile.
   *
   * M2.3.1 · sequenza ~5 secondi, adatta ad ambiente bar rumoroso:
   * 4 blocchi di 3 note WhatsApp-like distanziati, gain elevato ma senza
   * clipping. Test chirp e notifica reale usano IDENTICAMENTE questa funzione.
   */
  play() {
    if (this.state !== "running" || !this.ctx) return false;
    // M2.3.5 · NON blocchiamo su document.hidden.
    // Il chime è WebAudio (oscillatori), che il browser fa suonare anche
    // in background se AudioContext è running (sbloccato via user gesture
    // al primo click su /staff). Firefox/Chrome permettono WebAudio in tab
    // hidden — comportamento differente da <audio>/<video> HTML5.
    return this._chime5s();
  }

  _testChirp() { this._chime5s(); }

  _chime5s() {
    // 5 blocchi × ~0.95s → ~4.75s totali. Ogni blocco = 3 note (880/660/990Hz)
    // Gain leggermente crescente per attirare l'attenzione senza allarme.
    try {
      const ctx = this.ctx;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const now = ctx.currentTime;

      // Master gain per prevenire clipping quando le code delle note si sovrappongono.
      const master = ctx.createGain();
      master.gain.setValueAtTime(0.85, now);
      master.connect(ctx.destination);

      const blockLayout = [
        { f: 880, dt: 0.00, dur: 0.28, peak: 0.70 },
        { f: 660, dt: 0.30, dur: 0.28, peak: 0.65 },
        { f: 990, dt: 0.60, dur: 0.35, peak: 0.72 },
      ];
      const NUM_BLOCKS = 5;
      const BLOCK_SPACING = 0.95;

      for (let b = 0; b < NUM_BLOCKS; b++) {
        const blockStart = now + b * BLOCK_SPACING;
        // Leggero pattern di volume: 0.85, 0.9, 1.0, 0.95, 0.85
        const volMult = [0.85, 0.95, 1.0, 0.95, 0.85][b] ?? 0.9;
        for (const n of blockLayout) {
          const t = blockStart + n.dt;
          const g = ctx.createGain();
          g.gain.setValueAtTime(0.0001, t);
          g.gain.exponentialRampToValueAtTime(n.peak * volMult, t + 0.015);
          g.gain.exponentialRampToValueAtTime(0.0001, t + n.dur);
          g.connect(master);
          const o = ctx.createOscillator();
          o.type = "sine";
          o.frequency.setValueAtTime(n.f, t);
          o.connect(g);
          o.start(t);
          o.stop(t + n.dur + 0.02);
        }
      }
      return true;
    } catch {
      return false;
    }
  }
}

// Singleton condiviso in tutta l'app.
export const audioNotifications = (typeof window !== "undefined")
  ? (window.__sarabiAudio || (window.__sarabiAudio = new AudioNotifications()))
  : new AudioNotifications();

// Se preferenza persistita "on", tentiamo un pre-warm (non funzionerà finché
// non arriva un gesture, ma prepariamo il context). Lo stato sarà "suspended"
// finché l'utente non clicca "Abilita".
if (audioNotifications.state !== "off"
    && audioNotifications.state !== "unsupported") {
  try {
    const AC = (typeof window !== "undefined")
      && (window.AudioContext || window.webkitAudioContext);
    if (AC && !audioNotifications.ctx) {
      audioNotifications.ctx = new AC();
      audioNotifications._attachCtxStateListener();
      audioNotifications._setState(audioNotifications.ctx.state === "running"
        ? "running" : "suspended");
    }
  } catch { /* ignore */ }
}

// Cross-device recovery: al ritorno operativo, se AudioContext è caduto in
// suspended/interrupted, tentiamo un resume silenzioso. Se serve un gesture
// utente (iOS blocco schermo prolungato → interrupted), il tentativo fallirà
// e `AudioStatusBar` mostrerà "Attiva notifiche".
if (typeof window !== "undefined") {
  // Import lazy per evitare cicli tra audioNotifications ↔ lifecycle.
  import("./lifecycle").then(({ lifecycle }) => {
    lifecycle.on("recovery", () => {
      if (audioNotifications && typeof audioNotifications.tryResumeAfterRecovery === "function") {
        audioNotifications.tryResumeAfterRecovery().catch(() => { /* silent */ });
      }
    });
  }).catch(() => { /* silent */ });
}
